/**
 * CheckoutContext — single-line-item cart for the embed app's checkout flow.
 *
 * v1 holds at most one MockCheckoutLineItem at a time (mirrors the existing
 * /partners/v1/embedded/mock-checkout endpoint and the prior hard-coded
 * DEMO_ACTIVITY in CheckoutView). When `cart` is null, CheckoutView falls
 * back to the demo activity so empty-state previews still work.
 *
 * Two surface modes:
 *   - Standard: cart line item rendered + WG-themed contact form. submit
 *     calls submitMockCheckout.
 *   - Embedded URL: cart includes `embedded_url` → CheckoutView iframes
 *     the partner's external booking page (with WG header + "Back to chat"
 *     chrome). The partner's page owns the conversion; submitMockCheckout
 *     does NOT fire.
 *
 * Set via:
 *   - User-tap path: a `card`/`app_action` button with verb='prefill_checkout'
 *     dispatches setCart() then setView('checkout').
 *   - Auto-fire path: an `app_action` with auto=true does the same on mount.
 *
 * Out of scope for v1: multi-item carts, item editing/removal, discounts.
 * Add when the booking flow actually carries multiple line items.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { MockCheckoutLineItem } from '../api/checkoutApi';

export interface CheckoutContact {
  name?: string;
  email?: string;
  phone?: string;
}

/**
 * Provider-specific URL/postMessage detection patterns the iframe
 * inspector consumes to decide WHEN a partner-iframe booking has
 * succeeded, been cancelled, or is mid-checkout. Wire-shape mirrors
 * functions-py PROVIDER_DETECTION_PATTERNS so the embed app stays
 * provider-agnostic — only the rules cross the boundary, never the
 * provider name as logic.
 */
export interface DetectionPatterns {
  success_patterns?: string[];
  checkout_patterns?: string[];
  cancel_patterns?: string[];
  confirmation_code_params?: string[];
  /** Cooperating providers postMessage `{type: "...", ...}`; match here. */
  success_message_types?: string[];
}

export interface CheckoutCartItem extends MockCheckoutLineItem {
  /**
   * When set, CheckoutView iframes this URL instead of rendering the
   * standard form. Submit/pay logic is delegated to the partner page;
   * we just provide the chrome + a back-to-chat affordance.
   */
  embedded_url?: string;
  /** Currency for the line total (default USD if unset). */
  currency_code?: string;
  /** Provider routing — passed through to submitMockCheckout when set. */
  provider?: string;
  external_supplier_id?: string;
  /**
   * Provider-keyed iframe-inspector rules. Backend (functions-py
   * PROVIDER_DETECTION_PATTERNS) is the source of truth; the brain
   * passes them through prefill_checkout → app_action.args. Optional —
   * absent means the inspector falls back to the _default rule set.
   */
  detection_patterns?: DetectionPatterns;
  /** 'iframe' | 'redirect' | 'wg_internal' — drives CheckoutView surface. */
  booking_method?: string;
  /**
   * Server-side tracking session created by /book. The hand-off surface
   * polls it so a visitor who finished on a partner tab (one we can't
   * frame) still lands back on a confirmed state in our chrome instead of
   * a dead end.
   */
  tracking_session_id?: string;
  /** Stable id (per cart entry) — used by Itinerary + telemetry. */
  item_id?: string;
  /**
   * When true, CheckoutView shows a WG-controlled review step (date /
   * party size / optional name+email) BEFORE handing off to the
   * partner iframe. The review's submit calls `/chat/v1/{sid}/checkout-build`
   * to rebuild the URL with the visitor's chosen values, then clears
   * this flag so the iframe surface mounts. Set by `_emit_activity_cards`
   * on the brain side so card-tap → review → confirm → iframe (3 hops,
   * all in WG chrome until the very last) instead of card-tap → iframe
   * with maybe-wrong defaults (1 hop with no editing affordance).
   */
  requires_review?: boolean;
}

interface CheckoutContextValue {
  cart: CheckoutCartItem | null;
  prefillContact: CheckoutContact | null;
  setCart: (item: CheckoutCartItem | null) => void;
  setPrefillContact: (contact: CheckoutContact | null) => void;
  /** Reset both — called from the "book another" path post-confirmation. */
  clear: () => void;
}

const CheckoutContext = createContext<CheckoutContextValue | undefined>(
  undefined,
);

export const CheckoutProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [cart, setCart] = useState<CheckoutCartItem | null>(null);
  const [prefillContact, setPrefillContact] = useState<CheckoutContact | null>(
    null,
  );

  const clear = useCallback(() => {
    setCart(null);
    setPrefillContact(null);
  }, []);

  const value = useMemo(
    () => ({ cart, prefillContact, setCart, setPrefillContact, clear }),
    [cart, prefillContact, clear],
  );

  return (
    <CheckoutContext.Provider value={value}>
      {children}
    </CheckoutContext.Provider>
  );
};

export function useCheckout(): CheckoutContextValue {
  const ctx = useContext(CheckoutContext);
  if (!ctx) throw new Error('useCheckout must be used within CheckoutProvider');
  return ctx;
}
